import { parentCategoriesStyles } from "@eisinc/App/Widgets/CategoryTilesDisplay/CategoryTiles.styles";
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import { constants } from "@eisinc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import { Button } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect} from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps & InjectableCss;
    showMoreStyles?: InjectableCss;
    subCategoryResultText?: TypographyPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
        css: css`
            position: relative;
            overflow: hidden;
            display: flex;
            flex-wrap: wrap;
            transition: max-height 400ms ease-out;
        `,
    },
    showMoreStyles: {
        css: css`
            background: ${getColor("primary.contrast")};
            padding: 3px 0;
            margin-top: 10px;
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    subCategoryResultText: {
        size: 16,
        lineHeight: "19px",
        color: "text.main",
        css: css`
            margin-bottom: 20px;
            display: block;
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields }: Props) => {
    const [showHidden, setShowHidden] = React.useState(false);
    
    useEffect(() => {        
        typeof window !== "undefined" && typeof localStorage !== "undefined" &&
        window?.localStorage?.setItem(constants.localStorage.shouldLoadProductsForSubCategories, "true");
    }, []);

    let firstCategoryRow = [] as any;
    let hiddenCategoryRow = [] as any;

    const splitCategories = (categories: any) => {
        firstCategoryRow = categories.slice(0, 12);
        hiddenCategoryRow = categories.slice(12);
    };

    const expandAllTiles = () => {
        setShowHidden(true);
    };

    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    if (category && category.subCategoryIds?.length > 12) {
        splitCategories(category.subCategoryIds);
    }

    return (
        <>
            {category.subCategoryIds.length && (
                <Typography {...categoryDetailSubCategoriesStyles.subCategoryResultText}>
                    {translate(`Found ${category.subCategoryIds.length} categories under `)}
                    <Typography weight="bold">{category.name}</Typography>
                </Typography>
            )}
            <StyledWrapper {...styles.container}>
                {category.subCategoryIds.length && (
                    <GridContainer gap={20} {...parentCategoriesStyles.wrappingContainer}>
                        {category.subCategoryIds.length <= 12 &&
                            category.subCategoryIds.map(subCategoryId => (
                                <SubCategoryLink
                                    key={subCategoryId}
                                    categoryId={subCategoryId}
                                    showImages={fields.showImages}
                                />
                            ))}
                        {category.subCategoryIds.length > 12 &&
                            firstCategoryRow.map((id: string) => (
                                <SubCategoryLink key={id} categoryId={id} showImages={fields.showImages} />
                            ))}
                        {showHidden &&
                            hiddenCategoryRow.map((id: string) => (
                                <SubCategoryLink key={id} categoryId={id} showImages={fields.showImages} />
                            ))}
                    </GridContainer>
                )}
                {category.subCategoryIds?.length > 12 && !showHidden && (
                    <GridItem width={[12, 12, 12, 12, 12]} {...categoryDetailSubCategoriesStyles.showMoreStyles}>
                        <Button variant="secondary" onClick={expandAllTiles}>
                            {translate(`View More ${category.name} Categories`)}
                        </Button>
                    </GridItem>
                )}
            </StyledWrapper>
        </>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    settingsCollection,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    const customSettings = settingsCollection["customSettings"] as any;

    return (
        <GridItem width={[6, 4, 3, 2, 2]}>
            <Clickable href={category.path} {...parentCategoriesStyles.wrapperCategoryLink}>
                <StyledWrapper {...parentCategoriesStyles.categoryImageWrapper}>
                    {showImages && (
                        <LazyImage
                            src={
                                category.smallImagePath
                                    ? category.smallImagePath
                                    : customSettings?.mediumDefaultCategoryImage
                            }
                            altText={category.imageAltText}
                            {...parentCategoriesStyles.categoryImage}
                        ></LazyImage>
                    )}
                </StyledWrapper>
                <Typography
                    color="text.main"
                    {...parentCategoriesStyles.categoryText}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Typography>
            </Clickable>
        </GridItem>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
    settingsCollection: getSettingsCollection(state),
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
